/**
 * ClosedTradesCard — full table of the user's closed positions with CSV
 * export. Available to ALL tiers (individuals + institutions).
 *
 * Pulls from GET /api/positions?status=closed (already returns full closed
 * position docs with entry_price, exit_price, pnl, entry_ts, exit_ts,
 * close_reason).
 *
 * Columns: symbol, side, qty, entry, exit, PnL, opened, closed, reason.
 */
import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Download, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useSortableData } from "@/hooks/useSortableData";
import { SortableTh } from "@/components/ui/sortable-th";
import { signTone, toneClassName, signedPrefix } from "@/lib/numberColor";

const FMT_KES = (v) => v == null ? "—" : Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 });
const FMT_TS  = (v) => v ? new Date(v).toLocaleString() : "—";
const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

/**
 * bucket7DayPnl — buckets closed trades by their UTC exit-date across the
 * trailing 7 days (oldest→newest). Returns `[{day, label, pnl}, …]` for the
 * sparkline. Zero-cost aggregation off the state we already have.
 */
function bucket7DayPnl(rows) {
    const today = new Date();
    const buckets = [];
    for (let i = 6; i >= 0; i--) {
        const d = new Date(today.getTime() - i * 86400_000);
        const key = d.toISOString().slice(0, 10);
        buckets.push({ day: key, label: DOW[d.getUTCDay()], pnl: 0 });
    }
    const byKey = Object.fromEntries(buckets.map((b) => [b.day, b]));
    for (const r of rows || []) {
        const k = (r.exit_ts || "").slice(0, 10);
        if (byKey[k]) byKey[k].pnl += Number(r.pnl || 0);
    }
    return buckets;
}

export default function ClosedTradesCard() {
    const [rows, setRows]       = useState([]);
    const [loading, setLoading] = useState(true);
    const [expanded, setExpanded] = useState(false);   // show all vs top 10

    async function load() {
        setLoading(true);
        try {
            const { data } = await api.get("/positions?status=closed");
            // Newest first
            const sorted = (data || []).slice().sort((a, b) => (b.exit_ts || "").localeCompare(a.exit_ts || ""));
            setRows(sorted);
        } catch (e) {
            setRows([]);
        } finally { setLoading(false); }
    }

    useEffect(() => { load(); }, []);

    /**
     * Client-side CSV export — no backend round trip needed since we already
     * have the full rows in state.
     */
    function downloadCsv() {
        const header = "closed_at,symbol,side,qty,entry_price,exit_price,pnl_kes,opened_at,close_reason\n";
        const body = rows.map((r) => [
            r.exit_ts, r.symbol, r.side, r.qty,
            r.entry_price, r.exit_price, r.pnl,
            r.entry_ts, r.close_reason || "manual",
        ].map((v) => v == null ? "" : String(v).replace(/"/g, '""')).map((v) => v.includes(",") ? `"${v}"` : v).join(",")).join("\n");
        const blob = new Blob([header + body], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `melian_closed_trades_${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
    }

    const { sorted: sortedRows, sortKey, direction, requestSort } = useSortableData(rows, {
        accessors: {
            closed: (r) => r.exit_ts || "",
            symbol: (r) => r.symbol || "",
            side:   (r) => r.side || "",
            qty:    (r) => Number(r.qty || 0),
            entry:  (r) => Number(r.entry_price || 0),
            exit:   (r) => Number(r.exit_price || 0),
            pnl:    (r) => Number(r.pnl || 0),
            reason: (r) => r.close_reason || "",
        },
    });
    const shown = expanded ? sortedRows : sortedRows.slice(0, 10);

    // 7-day PnL sparkline — recomputes only when rows change.
    const sparkline = useMemo(() => {
        const buckets = bucket7DayPnl(rows);
        const maxAbs  = Math.max(1, ...buckets.map((b) => Math.abs(b.pnl)));
        const total   = buckets.reduce((s, b) => s + b.pnl, 0);
        return { buckets, maxAbs, total };
    }, [rows]);

    return (
        <div className="border border-border bg-card rounded" data-testid="closed-trades-card">
            {/* 7-day PnL sparkline — client-computed off `rows`, zero backend. */}
            {rows.length > 0 && (
                <div className="px-3 pt-3 pb-2 border-b border-border" data-testid="closed-trades-sparkline">
                    <div className="flex items-center justify-between mb-2">
                        <div className="overline">PnL · last 7 days</div>
                        <div className={`text-xs mono font-extrabold ${toneClassName(signTone(sparkline.total))}`}>
                            {signedPrefix(sparkline.total)}{FMT_KES(sparkline.total)} KES
                        </div>
                    </div>
                    <div className="flex items-end justify-between gap-1 h-12 relative">
                        {/* Zero axis line — bars grow up (positive) or down (negative) from here. */}
                        <div className="absolute left-0 right-0 top-1/2 border-t border-border/60 pointer-events-none" />
                        {sparkline.buckets.map((b) => {
                            const h = (Math.abs(b.pnl) / sparkline.maxAbs) * 22;   // half of 48px
                            const isPos = b.pnl >= 0;
                            const isZero = b.pnl === 0;
                            return (
                                <div
                                    key={b.day}
                                    className="flex-1 h-full relative group"
                                    title={`${b.label} · ${b.day} · ${FMT_KES(b.pnl)} KES`}
                                    data-testid={`pnl-bar-${b.day}`}
                                >
                                    {!isZero && (
                                        <div
                                            className={`absolute left-1/2 -translate-x-1/2 w-3/5 rounded-sm ${
                                                isPos ? "bg-success" : "bg-danger"
                                            } transition-colors`}
                                            style={
                                                isPos
                                                    ? { bottom: "50%", height: `${Math.max(2, h)}px`, opacity: 0.85 }
                                                    : { top: "50%", height: `${Math.max(2, h)}px`, opacity: 0.85 }
                                            }
                                        />
                                    )}
                                </div>
                            );
                        })}
                    </div>
                    <div className="flex justify-between mt-1 text-[10px] text-muted-foreground mono">
                        {sparkline.buckets.map((b) => (
                            <span key={b.day} className="flex-1 text-center">{b.label}</span>
                        ))}
                    </div>
                </div>
            )}

            <div className="flex items-center justify-between p-3 border-b border-border">
                <div>
                    <div className="overline">Closed trades</div>
                    <div className="text-[11px] text-muted-foreground">
                        {loading ? "Loading…" : `${rows.length} closed trade${rows.length === 1 ? "" : "s"}`}
                    </div>
                </div>
                <div className="flex items-center gap-1.5">
                    <Button size="sm" variant="ghost" onClick={load} disabled={loading} data-testid="closed-trades-reload">
                        <RefreshCw className="w-3.5 h-3.5" />
                    </Button>
                    <Button size="sm" variant="outline" onClick={downloadCsv} disabled={rows.length === 0} data-testid="closed-trades-export-csv">
                        <Download className="w-3.5 h-3.5 mr-1" /> Export CSV
                    </Button>
                </div>
            </div>

            <div className="overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="text-[11px] text-muted-foreground uppercase">
                        <tr className="border-b border-border">
                            <SortableTh label="Closed" sortKey="closed" currentKey={sortKey} direction={direction} onSort={requestSort} />
                            <SortableTh label="Symbol" sortKey="symbol" currentKey={sortKey} direction={direction} onSort={requestSort} />
                            <SortableTh label="Side"   sortKey="side"   currentKey={sortKey} direction={direction} onSort={requestSort} />
                            <SortableTh label="Qty"    sortKey="qty"    currentKey={sortKey} direction={direction} onSort={requestSort} align="right" />
                            <SortableTh label="Entry"  sortKey="entry"  currentKey={sortKey} direction={direction} onSort={requestSort} align="right" />
                            <SortableTh label="Exit"   sortKey="exit"   currentKey={sortKey} direction={direction} onSort={requestSort} align="right" />
                            <SortableTh label="PnL (KES)" sortKey="pnl" currentKey={sortKey} direction={direction} onSort={requestSort} align="right" />
                            <SortableTh label="Reason" sortKey="reason" currentKey={sortKey} direction={direction} onSort={requestSort} />
                        </tr>
                    </thead>
                    <tbody data-testid="closed-trades-body">
                        {shown.length === 0 && !loading && (
                            <tr>
                                <td colSpan="8" className="px-3 py-10 text-center text-muted-foreground">
                                    No closed trades yet. Once you close a position, it will appear here with full detail.
                                </td>
                            </tr>
                        )}
                        {shown.map((r) => (
                            <tr key={r.id} className="border-b border-border/50 hover:bg-secondary/40">
                                <td className="px-3 py-2 mono text-xs whitespace-nowrap">{FMT_TS(r.exit_ts)}</td>
                                <td className="px-3 py-2 mono font-bold">{r.symbol}</td>
                                <td className={`px-3 py-2 uppercase text-xs font-bold ${r.side === "buy" ? "text-success" : "text-danger"}`}>{r.side}</td>
                                <td className="px-3 py-2 mono text-right">{r.qty}</td>
                                <td className="px-3 py-2 mono text-right">{FMT_KES(r.entry_price)}</td>
                                <td className="px-3 py-2 mono text-right">{FMT_KES(r.exit_price)}</td>
                                <td className={`px-3 py-2 mono text-right font-bold ${toneClassName(signTone(r.pnl))}`}>
                                    {FMT_KES(r.pnl)}
                                </td>
                                <td className="px-3 py-2 text-xs text-muted-foreground">
                                    {r.close_reason === "stop_loss"
                                        ? <span className="text-amber-600 dark:text-amber-400 font-bold">Stop-loss</span>
                                        : "Manual"}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {rows.length > 10 && (
                <div className="p-2 border-t border-border text-center">
                    <button
                        onClick={() => setExpanded((v) => !v)}
                        className="text-xs font-bold uppercase tracking-wider text-primary hover:text-primary/80"
                        data-testid="closed-trades-toggle-expand"
                    >
                        {expanded ? `Collapse (showing all ${rows.length})` : `Show all ${rows.length} closed trades`}
                    </button>
                </div>
            )}
        </div>
    );
}
